<!--
  The connection to the Tax Authority (חשבוניות ישראל) at a glance - הגדרות → מסים (asked 5/11/2026:
  "a visible indication if the connection to רשות המסים is connected or not, in the settings, and
  when it expires, how long it is valid for"):
    - connected or not, from the newest token row (GET heshboniotisrael/auth-status?details=1);
    - when the consent was given, until when it holds and how many days are left (a consent holds
      90 days, then the business connects again - the app's banner warns 14 days ahead);
    - what the requests themselves say: the last document that got an allocation number, and the
      invoices after it that the Tax Authority refused on the authorization (a consent in date is no
      proof: since 1/11/2026 it refuses PlanUp's tokens for פפריקה whatever the consent);
    - "בדיקת החיבור" = check-connection: a token refresh + an empty Approval, the one call that
      knows. It calls the Tax Authority, so only on the click - never on the page load.
  The connect button moved in here from Settings.vue (the same authorize url).
-->
<template>

    <div class="taxAuthorityStatus">

        <div v-if="loading && !status"
             class="taxAuthorityStatusLoading">
            <i class="fa-solid fa-spinner fa-spin"></i>
            בודקים את מצב החיבור לרשות המסים...
        </div>

        <template v-else>

            <div class="taxAuthorityStatusHead">
                <span class="taxAuthorityStatusPill"
                      :class="'taxAuthorityStatusPill_' + state.level">
                    <span class="taxAuthorityStatusDot"></span>
                    {{ state.text }}
                </span>
            </div>

            <div v-if="loadFailed && !status"
                 class="taxAuthorityStatusLine taxAuthorityStatusMuted">
                לא ניתן לקרוא כרגע את מצב החיבור.
            </div>

            <div v-if="status && !status.connected"
                 class="taxAuthorityStatusLine taxAuthorityStatusMuted taxAuthorityNotConnectedNote">
                הרשאה מול רשות המסים בתוקף 90 ימים מיום מתן ההרשאה, ובסופם יש להתחבר מחדש.
            </div>

            <!-- a consent of the scope the Tax Authority stopped taking on 1/11/2026 (auth-status scopeOutdated) -->
            <div v-if="status && status.connected && status.scopeOutdated && !status.expired"
                 class="taxAuthorityStatusLine taxAuthorityScopeOutdated">
                <i class="fa-solid fa-triangle-exclamation"
                   style="color:#c92a2a;"></i>
                החיבור נעשה לפני השינוי של רשות המסים ב-1/11/2026 - יש להתחבר מחדש פעם אחת.
            </div>

            <table v-if="status && status.connected"
                   class="taxAuthorityStatusTable">
                <tr v-if="status.connectedAt">
                    <th>ההרשאה ניתנה</th>
                    <td class="taxAuthorityConnectedAt">{{ dateTime(status.connectedAt) }}</td>
                </tr>
                <tr v-if="status.expiresAt">
                    <th>{{ status.expired ? 'פגה בתאריך' : 'בתוקף עד' }}</th>
                    <td class="taxAuthorityExpiresAt">
                        {{ dateTime(status.expiresAt) }}
                        <span class="taxAuthorityDaysLeft"
                              :class="{ taxAuthorityDaysLeftSoon: !status.expired && status.daysRemaining <= 14, taxAuthorityDaysLeftGone: status.expired }">
                            {{ daysLeftText }}
                        </span>
                    </td>
                </tr>
                <tr>
                    <th>משך התוקף</th>
                    <td class="taxAuthorityValidFor">
                        {{ status.validDays || 90 }} ימים מיום מתן ההרשאה - בסופם יש להתחבר מחדש
                    </td>
                </tr>
            </table>

            <div v-if="status && status.lastAllocation"
                 class="taxAuthorityStatusLine taxAuthorityLastAllocation">
                <i class="fa-regular fa-circle-check"
                   style="color:#2e9e6b;"></i>
                מספר ההקצאה האחרון התקבל עבור
                <router-link :to="'/taxDocument/' + status.lastAllocation.taxDocumentId">{{ documentName(status.lastAllocation) }}</router-link>
                מ-{{ day(status.lastAllocation.issuedAt) }}
            </div>

            <div v-if="status && status.refusedSinceCount > 0"
                 class="taxAuthorityStatusLine taxAuthorityRefused">
                <i class="fa-solid fa-triangle-exclamation"
                   style="color:#c92a2a;"></i>
                <template v-if="status.refusedSinceCount == 1">
                    {{ status.lastAllocation ? 'מאז, ' : '' }}חשבונית אחת לא קיבלה מספר הקצאה כי רשות המסים לא אישרה את החיבור של PlanUp<template v-if="status.lastRefused">
                        (<router-link :to="'/taxDocument/' + status.lastRefused.taxDocumentId">{{ documentName(status.lastRefused) }}</router-link>
                        מ-{{ day(status.lastRefused.issuedAt) }})</template>.
                    אפשר לבקש עבורה מספר הקצאה מתוך המסמך כשהחיבור תקין.
                </template>
                <template v-else>
                    {{ status.lastAllocation ? 'מאז, ' : '' }}{{ status.refusedSinceCount }} חשבוניות לא קיבלו מספר הקצאה כי רשות המסים לא אישרה את החיבור של PlanUp<template v-if="status.lastRefused">
                        (האחרונה:
                        <router-link :to="'/taxDocument/' + status.lastRefused.taxDocumentId">{{ documentName(status.lastRefused) }}</router-link>
                        מ-{{ day(status.lastRefused.issuedAt) }})</template>.
                    אפשר לבקש עבורן מספר הקצאה מתוך המסמכים כשהחיבור תקין.
                </template>
            </div>

            <!-- the live check's answer -->
            <div v-if="check"
                 class="taxAuthorityCheckResult"
                 :class="'taxAuthorityCheckResult_' + check.status">
                <template v-if="check.status == 'ok'">
                    <i class="fa-solid fa-circle-check"></i>
                    החיבור תקין: רשות המסים מקבלת כרגע את הבקשות של PlanUp.
                </template>
                <template v-else-if="check.status == 'refused'">
                    <i class="fa-solid fa-ban"></i>
                    רשות המסים חוסמת כרגע את הגישה<span v-if="check.oauthErrorDescription"> ({{ check.oauthErrorDescription }})</span>.
                    זו לא בעיה בחיבור שלכם, ולכן התחברות מחדש לא תפתור אותה. לבירור: התמיכה של PlanUp.
                </template>
                <template v-else-if="check.status == 'reconnect'">
                    <i class="fa-solid fa-plug-circle-xmark"></i>
                    ההרשאה אינה תקפה - יש להתחבר מחדש לרשות המסים.
                </template>
                <template v-else>
                    <i class="fa-solid fa-circle-question"></i>
                    לא התקבלה תשובה מרשות המסים. אפשר לנסות שוב בעוד כמה דקות.
                </template>
                <span class="taxAuthorityCheckedAt">נבדק ב-{{ check.at }}</span>
            </div>

            <div class="taxAuthorityStatusButtons">
                <a :href="authorizeUrl">
                    <button type="button"
                            class="btn btn-primary">
                        {{ status && status.connected ? 'התחברות מחדש לרשות המסים' : 'מתן הרשאה באתר רשות המסים' }}
                    </button>
                </a>
                <button v-if="status && status.connected"
                        type="button"
                        class="btn btn-outline-primary taxAuthorityCheckButton"
                        :disabled="checking"
                        @click="runCheck">
                    <i v-if="checking"
                       class="fa-solid fa-spinner fa-spin"></i>
                    {{ checking ? 'בודקים מול רשות המסים...' : 'בדיקת החיבור מול רשות המסים' }}
                </button>
            </div>

        </template>

    </div>

</template>
<script>

import axiosV2 from '../../core/http/axios_api_v2';
import { checkTaxAuthorityConnection, getTaxAuthorityAuthorizeUrl } from '../../core/heshboniotIsrael';

function pad(n) {
    return (n < 10 ? '0' : '') + n;
}

export default {

    name: 'TaxAuthorityConnectionStatus',

    data() {
        return {
            status: null,
            loading: false,
            loadFailed: false,
            checking: false,
            check: null,          // { status, oauthErrorDescription, at }
        }
    },

    computed: {

        authorizeUrl() {
            return getTaxAuthorityAuthorizeUrl();
        },

        //the pill: the live check when there was one, else what is stored
        state() {
            let status = this.status;
            let check = this.check;

            if (check && check.status == 'ok')
                return { level: 'ok', text: 'מחובר לרשות המסים - החיבור תקין' };
            if (check && check.status == 'refused')
                return { level: 'bad', text: 'רשות המסים חוסמת כרגע את הגישה' };
            if (check && check.status == 'reconnect')
                return { level: 'bad', text: 'נדרשת התחברות מחדש לרשות המסים' };

            if (!status)
                return { level: 'none', text: 'מצב החיבור אינו ידוע' };
            if (!status.connected)
                return { level: 'none', text: 'לא מחובר לרשות המסים' };
            if (status.expired)
                return { level: 'bad', text: 'ההרשאה מול רשות המסים פגה' };
            if (status.scopeOutdated)
                return { level: 'bad', text: 'נדרשת התחברות מחדש לרשות המסים' };
            if (status.refusedSinceCount > 0)
                return { level: 'warn', text: 'מחובר, אבל רשות המסים דחתה בקשות אחרונות' };
            if (status.daysRemaining != null && status.daysRemaining <= 14)
                return { level: 'warn', text: 'מחובר לרשות המסים - ההרשאה תפוג בקרוב' };
            return { level: 'ok', text: 'מחובר לרשות המסים' };
        },

        daysLeftText() {
            let status = this.status;
            if (!status || status.daysRemaining == null) return '';
            if (status.expired) {
                let ago = -status.daysRemaining;
                if (ago <= 0) return '(היום)';
                if (ago == 1) return '(אתמול)';
                return '(לפני ' + ago + ' ימים)';
            }
            if (status.daysRemaining <= 1) return '(נותר פחות מיום)';
            return '(עוד ' + status.daysRemaining + ' ימים)';
        },
    },

    mounted() {
        this.load();
    },

    methods: {

        load() {
            this.loading = true;
            return axiosV2.get('heshboniotisrael/auth-status?details=1')
                .then(response => {
                    this.status = response && response.data ? response.data : null;
                    this.loadFailed = !this.status;
                })
                .catch(() => { this.loadFailed = true; })
                .then(() => { this.loading = false; });
        },

        runCheck() {
            if (this.checking) return;
            this.checking = true;
            checkTaxAuthorityConnection()
                .then(result => {
                    let now = new Date();
                    this.check = {
                        status: (result && result.status) || 'unknown',
                        oauthErrorDescription: (result && result.oauthErrorDescription) || '',
                        at: pad(now.getHours()) + ':' + pad(now.getMinutes()),
                    };
                })
                .catch(() => {
                    let now = new Date();
                    this.check = { status: 'unknown', oauthErrorDescription: '', at: pad(now.getHours()) + ':' + pad(now.getMinutes()) };
                })
                .then(() => {
                    this.checking = false;
                    //the check refreshed the token - read the row it wrote
                    return this.load();
                });
        },

        //ms epoch → 4/11/2026 08:26
        dateTime(ms) {
            let d = new Date(ms);
            if (isNaN(d.getTime())) return '';
            return d.getDate() + '/' + (d.getMonth() + 1) + '/' + d.getFullYear() + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes());
        },

        //'YYYY-MM-DD HH:MM' (Israel wall clock) → 4/11/2026
        day(value) {
            let match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
            return match ? (+match[3]) + '/' + (+match[2]) + '/' + match[1] : '';
        },

        documentName(doc) {
            let types = this.$store.state.taxDocumentTypes || [];
            let type = types.find(t => t.value == doc.type);
            return (type ? type.name : 'מסמך') + ' ' + doc.number;
        },
    },
}

</script>
<style>
.taxAuthorityStatus {
    border: solid 1px #ece7f7;
    background: #fbfaff;
    border-radius: 8px;
    padding: 14px 16px;
    margin-bottom: 14px;
    max-width: 680px;
}

.taxAuthorityStatusLoading {
    color: #888;
}

.taxAuthorityStatusHead {
    margin-bottom: 10px;
}

.taxAuthorityStatusPill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border-radius: 16px;
    padding: 4px 14px;
    font-weight: 600;
    font-size: 15px;
}

.taxAuthorityStatusDot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex: none;
}

.taxAuthorityStatusPill_ok {
    background: #e6f6ee;
    color: #1f7a50;
}

.taxAuthorityStatusPill_ok .taxAuthorityStatusDot {
    background: #2e9e6b;
}

.taxAuthorityStatusPill_warn {
    background: #fff4e5;
    color: #a24b00;
}

.taxAuthorityStatusPill_warn .taxAuthorityStatusDot {
    background: #e8590c;
}

.taxAuthorityStatusPill_bad {
    background: #fdecec;
    color: #a61e1e;
}

.taxAuthorityStatusPill_bad .taxAuthorityStatusDot {
    background: #e03131;
}

.taxAuthorityStatusPill_none {
    background: #f1f1f4;
    color: #555;
}

.taxAuthorityStatusPill_none .taxAuthorityStatusDot {
    background: #aaa;
}

.taxAuthorityStatusTable {
    border-collapse: collapse;
    margin-bottom: 8px;
}

.taxAuthorityStatusTable th {
    font-weight: 400;
    color: #777;
    padding: 3px 0 3px 18px;
    white-space: nowrap;
    vertical-align: top;
}

.taxAuthorityStatusTable td {
    padding: 3px 0;
    color: #2a3142;
}

.taxAuthorityDaysLeft {
    color: #2e9e6b;
    margin-right: 4px;
}

.taxAuthorityDaysLeftSoon {
    color: #e8590c;
    font-weight: 600;
}

.taxAuthorityDaysLeftGone {
    color: #c92a2a;
    font-weight: 600;
}

.taxAuthorityStatusLine {
    margin: 6px 0;
    line-height: 1.6;
}

.taxAuthorityStatusMuted {
    color: #888;
}

.taxAuthorityStatusLine a,
.taxAuthorityCheckResult a {
    color: #7c5cc4;
}

.taxAuthorityCheckResult {
    margin: 10px 0 4px 0;
    padding: 8px 12px;
    border-radius: 6px;
    line-height: 1.6;
}

.taxAuthorityCheckResult i {
    margin-left: 4px;
}

.taxAuthorityCheckResult_ok {
    background: #e6f6ee;
    color: #1f7a50;
}

.taxAuthorityCheckResult_refused,
.taxAuthorityCheckResult_reconnect {
    background: #fdecec;
    color: #a61e1e;
}

.taxAuthorityCheckResult_unknown {
    background: #f1f1f4;
    color: #555;
}

.taxAuthorityCheckedAt {
    color: #999;
    font-size: 12px;
    margin-right: 6px;
}

.taxAuthorityStatusButtons {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
}
</style>
